import { Abas } from '@core/components/Abas';
import { CancelButton, SaveButton } from '@core/components/Buttons';
import { FormDialogContent, SimpleDialog } from '@core/components/Dialog';
import { mensagemMinimo3Caracteres } from '@core/util/mensagemUtil';
import { cancelarAlert } from 'app/services/alerts';
import { validateCnpj, validateCpf } from 'app/utils/validation';
import { FormikProvider, useFormik } from 'formik';
import moment from 'moment';
import React, { useContext, useEffect, useState } from 'react';
import { FaUserTag } from 'react-icons/fa';
import * as yup from 'yup';
import { VendedorContext } from '../../../VendedorContext';
import { vendedorModais } from '../vendedorModais';
import { TabContaBancariaVendedor, TabContatoVendedor, TabEnderecoVendedor } from './components';
import TabDadosGeraisVendedor from './components/TabDadosGeraisVendedor';

const vendedorAbas = {
  dadosGerais: 0,
  endereco: 1,
  contato: 2,
  contaBancaria: 3,
};

const CadastroVendedorModal = ({ onSubmit, fetchPapel }) => {
  const [vendedor] = useContext(VendedorContext).vendedorAtual;
  const [modal, setModal] = useContext(VendedorContext).modal;
  const [page] = useContext(VendedorContext).page;
  const [size] = useContext(VendedorContext).size;
  const [abaSelecionada, setAbaSelecionada] = useState(vendedorAbas.dadosGerais);

  const vendedorPessoaFisicaInitialValues = {
    id: vendedor?.pessoa?.pessoaFisica?.id || '',
    cpf: vendedor?.pessoa?.pessoaFisica?.cpf || '',
    identidade: vendedor?.pessoa?.pessoaFisica?.identidade || '',
    dataNascimento: vendedor?.pessoa?.pessoaFisica?.dataNascimento
      ? moment(vendedor?.pessoa?.pessoaFisica?.dataNascimento)
      : null,
    identificadorEstrangeiro: vendedor?.pessoa?.pessoaFisica?.identificadorEstrangeiro || '',
  };

  const vendedorPessoaJuridicaInitialValues = {
    id: vendedor?.pessoa?.pessoaJuridica?.id || '',
    indicadorInscricaoEstadual: vendedor?.pessoa?.pessoaJuridica?.indicadorInscricaoEstadual || '',
    cnpj: vendedor?.pessoa?.pessoaJuridica?.cnpj || '',
    razaoSocial: vendedor?.pessoa?.pessoaJuridica?.razaoSocial || '',
    dataFundacao: vendedor?.pessoa?.pessoaJuridica?.dataFundacao
      ? moment(vendedor.pessoa.pessoaJuridica.dataFundacao)
      : null,
    inscricaoEstadual: vendedor?.pessoa?.pessoaJuridica?.inscricaoEstadual || '',
  };

  const enderecoInitialValues = {
    id: vendedor?.pessoa?.endereco?.id || '',
    cep: vendedor?.pessoa?.endereco?.cep || '',
    logradouro: vendedor?.pessoa?.endereco?.logradouro || '',
    numero: vendedor?.pessoa?.endereco?.numero || '',
    complemento: vendedor?.pessoa?.endereco?.complemento || '',
    uf: vendedor?.pessoa?.endereco?.uf || '',
    cidade: vendedor?.pessoa?.endereco?.cidade || '',
    bairro: vendedor?.pessoa?.endereco?.bairro || '',
    referencia: vendedor?.pessoa?.endereco?.referencia || '',
  };

  const contatoInitialValues = [
    {
      id: vendedor?.pessoa?.contatos?.[0]?.id || '',
      email: vendedor?.pessoa?.contatos?.[0]?.email || '',
      nomeReferencia: vendedor?.pessoa?.contatos?.[0]?.nomeReferencia || '',
      numero: vendedor?.pessoa?.contatos?.[0]?.numero || '',
      telefonesAdicionais: vendedor?.pessoa?.contatos?.[0]?.telefonesAdicionais || [],
      emailsAdicionais: vendedor?.pessoa?.contatos?.[0]?.emailsAdicionais || [],
    },
  ];

  const contaInitialValues = {
    id: vendedor?.pessoa?.conta?.id || '',
    contaBancaria: vendedor?.pessoa?.conta?.contaBancaria || {},
    descricao: vendedor?.pessoa?.conta?.descricao || '',
    tipo: vendedor?.pessoa?.conta?.tipo || '',
  };

  const initialValues = {
    id: vendedor?.id || '',
    comissao: vendedor?.comissao,
    possuiVinculo: vendedor?.possuiVinculo,
    pessoa: {
      id: vendedor?.pessoa?.id || '',
      nome: vendedor?.pessoa?.nome || '',
      tipoPessoa: vendedor?.pessoa?.tipoPessoa || 'FISICA',
      inscricaoMunicipal: vendedor?.pessoa?.inscricaoMunicipal || '',
      pessoaFisica: vendedorPessoaFisicaInitialValues,
      pessoaJuridica: vendedorPessoaJuridicaInitialValues,
      contatos: contatoInitialValues,
      endereco: enderecoInitialValues,
      conta: contaInitialValues,
    },
  };

  const schemaEndereco = yup
    .object()
    .shape({
      bairro: yup.string().min(3, mensagemMinimo3Caracteres),
      referencia: yup.string().min(3, mensagemMinimo3Caracteres),
    })
    .nullable();

  const schemaPessoaFisica = yup
    .object()
    .shape({
      cpf: yup.string().test('test cpf', 'CPF inválido', (value) => {
        if (value) {
          const validate = validateCpf(value);
          return validate;
        }
        return true;
      }),
      identidade: yup.string().min(3, mensagemMinimo3Caracteres),
      identificadorEstrangeiro: yup.string().min(3, mensagemMinimo3Caracteres),
    })
    .nullable();

  const schemaPessoaJuridica = yup
    .object()
    .shape({
      cnpj: yup.string().test('test cnpj', 'CNPJ inválido', (value) => {
        if (value) {
          const validate = validateCnpj(value);
          return validate;
        }
        return true;
      }),
      razaoSocial: yup.string().min(3, mensagemMinimo3Caracteres),
      inscricaoEstadual: yup.string().min(3, mensagemMinimo3Caracteres),
    })
    .nullable();

  const schemaContatos = yup.array().of(
    yup.object().shape({
      nomeReferencia: yup.string().min(3, mensagemMinimo3Caracteres),
      email: yup.string().email('E-mail inválido'),
      emailsAdicionais: yup.array().of(
        yup.object().shape({
          email: yup.string().email('E-mail inválido'),
        })
      ),
    })
  );

  const schemaConta = yup
    .object()
    .shape({
      descricao: yup.string().min(3, mensagemMinimo3Caracteres),
      contaBancaria: yup.object().shape({
        agencia: yup.number().nullable(),
        agenciaDigito: yup.number().nullable(),
        numeroConta: yup.number().nullable(),
        contaDigito: yup.number().nullable(),
        banco: yup.object().nullable(),
      }),
      tipoConta: yup
        .string()
        .nullable()
        .test('tipo.required', 'Informe o tipo da conta', function isTipoContaInformado(value) {
          const {
            contaBancaria: { agencia, agenciaDigito, numeroConta, contaDigito, banco, tipoConta },
            descricao,
          } = this.parent;

          if (descricao || agencia || agenciaDigito || numeroConta || contaDigito || banco?.id) {
            return !!value || !!tipoConta;
          }

          return true;
        }),
    })
    .nullable();

  const vendedorSchema = yup.object().shape({
    pessoa: yup
      .object()
      .default({ nome: '', tipoPessoa: 'JURIDICA' })
      .shape({
        nome: yup
          .string()
          .required('Informe o nome')
          .min(3, mensagemMinimo3Caracteres),
        tipoPessoa: yup.string().required(),
        inscricaoMunicipal: yup.string().min(3, mensagemMinimo3Caracteres),
        pessoaFisica: schemaPessoaFisica,
        pessoaJuridica: schemaPessoaJuridica,
        contatos: schemaContatos,
        endereco: schemaEndereco,
        conta: schemaConta,
      })
      .required(),
  });

  const close = () => {
    formik.resetForm(initialValues);
    setAbaSelecionada(vendedorAbas.dadosGerais);
    setModal('');
  };

  const handleSubmit = async (data) => {
    if (!formik?.dirty) {
      close();
      return;
    }

    const _vendedor = JSON.parse(JSON.stringify(data));

    if (_vendedor.pessoa?.conta?.contaBancaria?.tipoConta) {
      _vendedor.pessoa.conta.tipoConta = _vendedor.pessoa.conta.contaBancaria.tipoConta;
    }

    if (_vendedor?.pessoa?.tipoPessoa === 'FISICA') {
      delete _vendedor.pessoa.pessoaJuridica;
    } else {
      delete _vendedor.pessoa.pessoaFisica;
    }

    // tipo conta é obrigatório se algum dado de conta for preenchido.
    // se n tiver tipoConta, não possui nenhum dado de conta
    if (!_vendedor?.pessoa?.conta?.tipoConta) {
      delete _vendedor.pessoa.conta;
    }

    // se n tem nenhum dado preenchido de endereço
    if (!Object.values(_vendedor?.pessoa?.endereco || {}).some((value) => !!value)) {
      delete _vendedor.pessoa.endereco;
    }

    const res = await onSubmit(_vendedor, { q: '', page, size });

    if (res) {
      close();
    }
  };

  const handleClose = () => {
    if (formik.dirty) {
      cancelarAlert({
        confirmar: close,
      });
    } else {
      close();
    }
  };

  const formik = useFormik({
    validationSchema: vendedorSchema,
    initialValues,
    onSubmit: handleSubmit,
    validateOnChange: true,
    validateOnBlur: true,
  });

  const handleChangeTipoPessoa = (tipoPessoa) => {
    if (tipoPessoa === 'FISICA') {
      formik.setFieldValue('pessoaJuridica', null);
      formik.setFieldValue('pessoa.pessoaFisica', vendedorPessoaFisicaInitialValues);
    } else {
      formik.setFieldValue('pessoaFisica', null);
      formik.setFieldValue('pessoa.pessoaFisica', vendedorPessoaJuridicaInitialValues);
    }
  };

  useEffect(() => {
    formik.resetForm(initialValues);
    // Efeito reativo apenas ao gatilho acima; incluir as demais reiniciaria o formulário durante a edição.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vendedor]);

  return (
    <SimpleDialog fullScreen open={modal === vendedorModais.cadastroVendedor} onClose={handleClose} innerScroll>
      <FormikProvider value={formik}>
        <FormDialogContent
          onSubmit={formik.handleSubmit}
          fullScreen
          header={{
            title: 'Cadastro de vendedor',
            icon: <FaUserTag />,
            toolbar: (
              <>
                <CancelButton title="cancelar" type="button" onClick={handleClose}>
                  Cancelar
                </CancelButton>
                <SaveButton color="buttonSave" type="submit">
                  Salvar
                </SaveButton>
              </>
            ),
          }}
          contentToolbar={
            <Abas
              value={abaSelecionada}
              onChange={setAbaSelecionada}
              tabs={[
                {
                  label: 'Dados gerais',
                  hasError: () => {
                    return false;
                  },
                },
                {
                  label: 'Endereço',
                  hasError: () => {
                    return false;
                  },
                },
                {
                  label: 'Contato',
                  hasError: () => {
                    return false;
                  },
                },
                {
                  label: 'Conta bancária',
                  hasError: () => {
                    return false;
                  },
                },
              ]}
            />
          }
          content={
            <div className="h-full max-h-full w-full">
              {abaSelecionada === vendedorAbas.dadosGerais && (
                <TabDadosGeraisVendedor onChangeTipoPessoa={handleChangeTipoPessoa} fetchPapel={fetchPapel} />
              )}
              {abaSelecionada === vendedorAbas.endereco && <TabEnderecoVendedor />}
              {abaSelecionada === vendedorAbas.contato && <TabContatoVendedor />}
              {abaSelecionada === vendedorAbas.contaBancaria && (
                <TabContaBancariaVendedor
                  onBlur={formik.handleBlur}
                  onChange={formik.handleChange}
                  values={formik.values.pessoa?.conta}
                  touched={formik.touched}
                  errors={formik.errors}
                  submitCount={formik.submitCount}
                />
              )}
            </div>
          }
        />
      </FormikProvider>
    </SimpleDialog>
  );
};

export default CadastroVendedorModal;
